import { useState, useCallback, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Activity, Building2, CalendarCheck, CheckCircle2, CreditCard, Gauge, Lock, MessageCircle, Users, Wrench, Zap, Radio, Clock, ShieldCheck, ArrowRight, Phone, MapPin, Sparkles, Mail, Send, X, Star, PackageCheck, ShoppingCart, Menu, KeyRound, Calculator } from "lucide-react";
import MacJitLogo from "../components/MacJitLogo";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { moduleHome } from "../lib/subscriptionModules";
import { roleHomePath } from "../lib/roles";

const PRODUCT_PLANS = [
  {
    key: "terminal_ledger",
    name: "Mini",
    price: "₹99/mo",
    offer: "99 free transactions · 10GB included",
    meta: "Billing + Razorpay payments + invoices",
    bestFor: "Small shops that need fast billing, payment links, refunds, and transaction history",
    loop: "",
    points: ["Counter billing", "Razorpay payment links", "Invoice email after payment", "Refund and transaction history", "10GB storage included"],
    available: true,
  },
  {
    key: "shop_inventory",
    name: "Shop Start",
    price: "₹999/mo",
    offer: "Launch offer: 14-day trial",
    meta: "Shopkeeper + Inventory",
    bestFor: "Small parts counters and single-person shops",
    loop: "Loop activates when staff count reaches 2+",
    points: ["Inventory control", "Low stock visibility", "Shopkeeper terminal", "Staff limit control"],
    available: false,
  },
  {
    key: "parts_seller",
    name: "Parts Seller",
    price: "₹1,499/mo",
    offer: "Sell local parts with location-aware leads",
    meta: "Shop + Inventory + Online parts",
    bestFor: "Parts sellers who want public booking requests",
    loop: "Loop activates when staff count reaches 2+",
    points: ["Global parts listing", "City/pincode discovery", "Product booking lock", "Seller-routed requests"],
    available: false,
  },
  {
    key: "garage_core",
    name: "Garage Core",
    price: "₹2,499/mo",
    offer: "Best for service workflow setup",
    meta: "Garage + Inventory",
    bestFor: "Workshops focused on service operations",
    loop: "Loop activates when staff count reaches 2+",
    points: ["Reception booking", "Mechanic queue", "QA/testing flow", "Customer tracking"],
    available: false,
  },
  {
    key: "garage_shop",
    name: "Garage Shop",
    price: "₹3,499/mo",
    offer: "Service and counter sales together",
    meta: "Garage + Inventory + Shop",
    bestFor: "Garages that service vehicles and sell parts",
    loop: "Loop included for teams with 2+ staff",
    points: ["Billing and payments", "Parts used in jobs", "Shopkeeper module", "Role-aware staff access"],
    available: false,
  },
  {
    key: "terminal_plus",
    name: "Terminal Pro",
    price: "₹399/mo",
    offer: "10GB free · 50 services · 99 shop transactions",
    meta: "Garage + shop + Loop + payments",
    bestFor: "Automobile teams that need service booking, shop billing, staff flow, and customer tracking",
    loop: "Loop included for teams with 2+ staff",
    points: ["50 service bookings", "99 shopkeeper transactions", "10GB included storage", "Merchant-owned Razorpay setup", "Loop, inventory, and service ops"],
    available: true,
  },
];

const HERO_MODULE_SLIDES = [
  {
    key: "mechanic",
    eyebrow: "Service floor",
    title: "Mechanic console",
    meta: "Parts cart, QA handoff, job state",
    render: "mechanic",
  },
  {
    key: "reception",
    eyebrow: "Command desk",
    title: "Reception control",
    meta: "Bookings, billing, payment links",
    render: "reception",
  },
  {
    key: "tracker",
    eyebrow: "Customer trust",
    title: "Secure car tracker",
    meta: "Email OTP, live progress, visits",
    render: "tracker",
  },
  {
    key: "communication",
    eyebrow: "Internal updates",
    title: "Team communication",
    meta: "Private files, staff alerts, support",
    render: "communication",
  },
];

const INITIAL_TORQUE_MESSAGE = {
  from: "bot",
  text: "Hi, I am Torque from MacJit. Tell me what is happening with your car, and I will guide you step by step.",
};

export default function LandingPage() {
  const nav = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [heroSlide, setHeroSlide] = useState(0);
  const [supportOpen, setSupportOpen] = useState(false);
  const [support, setSupport] = useState({ name: "", email: "", phone: "", message: "" });
  const [supportConversation, setSupportConversation] = useState(null);
  const [supportText, setSupportText] = useState("");
  const [supportBusy, setSupportBusy] = useState(false);
  const [checkoutPlan, setCheckoutPlan] = useState(null);
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const [loginModal, setLoginModal] = useState(null);
  const [landingStats, setLandingStats] = useState({ cars_serviced: 1400, on_time_pct: 98.6, average_rating: 4.9, latest_review: {} });
  const [stockItems, setStockItems] = useState([]);
  const goStaff = () => nav("/terminal");
  const openProductLogin = useCallback((product) => {
    const target = product === "mini" ? "/mini" : "/terminal";
    const isMobile = typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;
    if (isMobile) {
      setLoginModal({
        title: product === "mini" ? "Mini login" : "Terminal login",
        product,
        path: target,
      });
      return;
    }
    nav(target);
  }, [nav]);
  const normalizeSupportPhone = (value) => {
    const digits = String(value || "").replace(/\D/g, "");
    if (!digits) return "";
    if (digits.startsWith("91") && digits.length > 10) return `+${digits.slice(0, 12)}`;
    return `+91${digits.slice(0, 10)}`;
  };
  const validSupportEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(value || "").trim());
  const validSupportPhone = (value) => /^\+91[6-9]\d{9}$/.test(normalizeSupportPhone(value));

  useEffect(() => {
    api.get("/public/landing").then((r) => setLandingStats(r.data)).catch(() => {});
    api.get("/public/inventory/in-stock").then((r) => setStockItems(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setHeroSlide((current) => (current + 1) % HERO_MODULE_SLIDES.length);
    }, 3600);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!supportOpen || !supportConversation?.id) return undefined;
    const load = () => {
      api.get(`/public/support/conversations/${supportConversation.id}`)
        .then((res) => setSupportConversation(res.data))
        .catch(() => {});
    };
    load();
    const timer = setInterval(load, 3000);
    return () => clearInterval(timer);
  }, [supportOpen, supportConversation?.id]);

  const submitSupport = async (e) => {
    e?.preventDefault();
    setSupportBusy(true);
    try {
      if (!supportConversation) {
        if (!support.name.trim()) return toast.error("Name is required");
        if (!validSupportEmail(support.email)) return toast.error("Enter a valid email address");
        const phone = normalizeSupportPhone(support.phone);
        if (!validSupportPhone(phone)) return toast.error("Enter a valid Indian mobile number");
        if (!support.message.trim()) return toast.error("Tell us what you need help with");
        const res = await api.post("/public/support/conversations", {
          name: support.name.trim(),
          email: support.email.trim().toLowerCase(),
          phone,
          message: support.message.trim(),
        });
        setSupportConversation(res.data);
        setSupport({ name: support.name, email: support.email.trim().toLowerCase(), phone, message: "" });
        toast.success(res.data?.support_online ? "Support is online" : "Support is offline, saved as enquiry");
      } else {
        if (!supportText.trim()) return;
        const res = await api.post(`/public/support/conversations/${supportConversation.id}/messages`, { message: supportText.trim() });
        setSupportConversation(res.data);
        setSupportText("");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send support message");
    } finally {
      setSupportBusy(false);
    }
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);
  const openCheckout = (plan, mode = "paid") => setCheckoutPlan({ ...plan, checkoutMode: mode });
  const submitCheckout = async (payload) => {
    setCheckoutBusy(true);
    try {
      const res = await api.post("/public/checkout", payload);
      const url = res.data?.payment_url || res.data?.terminal_url;
      toast.success(res.data?.mock_payment_enabled ? "Local payment mode ready" : "Secure checkout created");
      if (url) {
        const handled = navigateInsideApp(url, nav);
        if (!handled) window.location.href = url;
      } else {
        setCheckoutPlan(null);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout");
    } finally {
      setCheckoutBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      {/* Nav */}
      <nav className="sticky top-0 z-30 bg-zinc-950/85 backdrop-blur-xl border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <MacJitLogo size={36} />
            <div className="leading-none shrink-0">
              <span className="font-display font-black text-xl tracking-tighter block"><span className="text-white">MAC</span><span className="text-orange-500">JIT</span></span>
              <span className="font-mono text-[9px] uppercase tracking-[0.24em] text-orange-500/80">Mechanic Just In Time</span>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-6 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">
            <a href="#shop" className="hover:text-orange-500">Parts</a>
            <a href="#product" className="hover:text-orange-500">Product</a>
            <a href="#enquiry" className="hover:text-orange-500">Enquire</a>
            <a href="#contact" className="hover:text-orange-500">Contact</a>
          </div>
          <div className="md:hidden flex items-center gap-2 min-w-0">
            <button
              type="button"
              onClick={() => setMobileMenuOpen((v) => !v)}
              aria-label="Toggle navigation menu"
              className="border border-orange-500/70 bg-orange-500/10 text-orange-500 hover:border-orange-400 hover:bg-orange-500 hover:text-black p-2.5 transition-colors"
            >
              {mobileMenuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-zinc-900 bg-zinc-950/98 px-4 py-4">
            <div className="grid gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-300">
              <a onClick={closeMobileMenu} href="#shop" className="border border-zinc-800 px-4 py-3 hover:border-orange-500 hover:text-orange-500">Parts</a>
              <button onClick={() => { closeMobileMenu(); nav("/parts"); }} className="text-left border border-zinc-800 px-4 py-3 hover:border-orange-500 hover:text-orange-500 uppercase tracking-[0.2em]">Browse Parts</button>
              <a onClick={closeMobileMenu} href="#product" className="border border-zinc-800 px-4 py-3 hover:border-orange-500 hover:text-orange-500">Product</a>
              <a onClick={closeMobileMenu} href="#enquiry" className="border border-zinc-800 px-4 py-3 hover:border-orange-500 hover:text-orange-500">Enquire</a>
              <a onClick={closeMobileMenu} href="#contact" className="border border-zinc-800 px-4 py-3 hover:border-orange-500 hover:text-orange-500">Contact</a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="relative grid-bg overflow-hidden reveal-up">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(249,115,22,0.18),transparent_35%),radial-gradient(circle_at_80%_0%,rgba(251,146,60,0.14),transparent_30%)] pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-zinc-950 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 pt-8 pb-12 sm:px-6 sm:pt-12 sm:pb-16 lg:pt-14 lg:pb-20 grid lg:grid-cols-12 gap-8 items-end relative">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 border border-orange-500/40 bg-orange-500/5 px-3 py-1.5 mb-8">
              <span className="w-1.5 h-1.5 bg-orange-500 rounded-full" />
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Automobile operations platform</p>
            </div>
            <h1 className="font-display font-black text-4xl sm:text-7xl lg:text-8xl tracking-tighter leading-[0.92] uppercase">
              Building<br /><span className="text-orange-500">Terminal</span><br />for mobility teams.
            </h1>
            <p className="mt-5 sm:mt-8 text-zinc-300 font-mono text-sm sm:text-base max-w-xl leading-relaxed">
              MacJit helps automobile businesses move from scattered calls, paper registers, and manual follow-ups to one connected operating system for service, parts, people, billing, and customer trust.
            </p>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 sm:mt-12 sm:gap-x-10">
              <span className="flex items-center gap-2"><Zap className="w-3 h-3 text-orange-500" />Garage OS</span>
              <span className="flex items-center gap-2"><Radio className="w-3 h-3 text-orange-500" />Parts marketplace</span>
              <span className="flex items-center gap-2"><ShieldCheck className="w-3 h-3 text-orange-500" />Tenant-ready product</span>
            </div>
          </div>
          <div className="hidden lg:col-span-5 sm:block">
            <HeroModuleCarousel
              slides={HERO_MODULE_SLIDES}
              active={heroSlide}
              setActive={setHeroSlide}
            />
          </div>
        </div>
      </section>

      <ProductPlatformSection onCheckout={openCheckout} onOpenLogin={openProductLogin} />

      <section className="bg-black px-5 pb-14 sm:hidden">
        <HeroModuleCarousel
          slides={HERO_MODULE_SLIDES}
          active={heroSlide}
          setActive={setHeroSlide}
          compact
        />
      </section>

      {/* Shop teaser */}
      <section id="shop" className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12 items-start reveal-up reveal-delay-1">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500 mb-2">01 — Parts marketplace</p>
          <h2 className="font-display font-black text-5xl lg:text-6xl tracking-tighter uppercase">Parts in<br />stock now.</h2>
          <p className="text-zinc-400 font-mono text-sm mt-6 max-w-md">
            Genuine parts from the same inventory our workshop uses. Request a part with your name and number; our team will connect soon to confirm pickup.
          </p>
          <button onClick={() => nav("/parts")} className="interactive-lift mt-8 bg-orange-500 hover:bg-orange-400 text-black font-display font-black uppercase tracking-widest px-6 py-3 transition-colors flex items-center gap-2">
            <ShoppingCart className="w-4 h-4" /> Browse parts <ArrowRight className="w-4 h-4" />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-px bg-zinc-900 border border-zinc-800">
          {(stockItems.length ? stockItems.slice(0, 4) : [
            { name: "Engine Oil 1L", price: 350, category: "Lubricants", stock: 4 },
            { name: "Brake Shoe Set", price: 420, category: "Brakes", stock: 3 },
            { name: "Air Filter", price: 280, category: "Filters", stock: 5 },
            { name: "Spark Plug", price: 150, category: "Ignition", stock: 8 },
          ]).map((it) => (
            <div key={it.id || it.name} className="bg-zinc-950 p-5 hover-soft">
              <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{it.category || "Stock"}</p>
              <p className="font-display font-bold mt-1">{it.name}</p>
              <p className="font-display font-black text-2xl text-orange-500 mt-3">₹{it.price}</p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-600 mt-1">{it.stock} in stock</p>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonial */}
      <section className="bg-orange-500 text-black reveal-up">
        <div className="max-w-5xl mx-auto px-6 py-20">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] mb-6">/ what Terminal users say</p>
          <p className="font-display font-black text-3xl lg:text-5xl tracking-tighter leading-tight">
            "Terminal made daily garage work much easier. Bookings, stock, staff updates, billing, payment links, and customer tracking stay in one place, so the team handles more customers without constant follow-up."
          </p>
          <div className="mt-8 flex items-center gap-3">
            <div className="w-10 h-10 bg-black flex items-center justify-center text-orange-500 font-display font-black">R</div>
            <div>
              <p className="font-display font-black">Repair shop owner</p>
              <p className="font-mono text-[10px] uppercase tracking-widest flex items-center gap-1">
                <Star className="w-3 h-3 fill-black" /> {landingStats.average_rating || 4.9}/5 Terminal product rating
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Enquiry */}
      <section id="enquiry" className="max-w-7xl mx-auto px-6 py-24 reveal-up reveal-delay-1">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500 mb-2">03 — Terminal trial</p>
            <h2 className="font-display font-black text-5xl lg:text-6xl tracking-tighter uppercase">Request a<br /><span className="text-orange-500">trial.</span></h2>
            <p className="text-zinc-400 font-mono text-sm mt-6 leading-relaxed">Choose the Terminal plan you want to explore. This request goes to the platform owner, and we will email your request ID after submission.</p>
            <div className="mt-8 space-y-3">
              <a href="tel:+919353401156" className="flex items-center gap-3 group">
                <div className="w-10 h-10 border border-zinc-800 group-hover:border-orange-500 grid place-items-center"><Phone className="w-4 h-4 text-orange-500" /></div>
                <div><p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Call</p><p className="font-display font-bold">+91 93534 01156</p></div>
              </a>
              <a href="mailto:hello@macjit.com" className="flex items-center gap-3 group">
                <div className="w-10 h-10 border border-zinc-800 group-hover:border-orange-500 grid place-items-center"><Mail className="w-4 h-4 text-orange-500" /></div>
                <div><p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Email</p><p className="font-display font-bold">hello@macjit.com</p></div>
              </a>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 border border-zinc-800 grid place-items-center"><MapPin className="w-4 h-4 text-orange-500" /></div>
                <div><p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Visit</p><p className="font-display font-bold">Varthur, Bangalore — 560087</p></div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <EnquiryForm />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="border-t border-zinc-900 bg-black">
        <div className="max-w-7xl mx-auto px-6 py-12 grid md:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <MacJitLogo size={32} />
              <span className="font-display font-black text-xl tracking-tighter"><span className="text-white">MAC</span><span className="text-orange-500">JIT</span></span>
            </div>
            <div className="font-mono text-xs text-zinc-500 leading-relaxed">
              <p><span className="font-display text-base font-black uppercase tracking-tight text-white">Terminal</span> for automobile businesses.</p>
              <p className="mt-2 flex flex-wrap gap-x-2 gap-y-1">
                {["Service", "Parts", "Staff", "Billing", "Customer trust"].map((word) => (
                  <span key={word} className="border border-orange-500/30 bg-orange-500/5 px-2 py-1 text-[10px] uppercase tracking-widest text-orange-500">{word}</span>
                ))}
              </p>
              <p className="mt-2 text-zinc-600">One operating system for the floor, counter, and customer.</p>
            </div>
            <p className="font-mono text-[10px] text-zinc-600 mt-3">macjit.com</p>
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-600 mb-3">Visit</p>
            <p className="font-mono text-xs text-zinc-300 flex items-start gap-2"><MapPin className="w-3 h-3 mt-1 text-orange-500" />Varthur,<br />Bangalore — 560087</p>
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-600 mb-3">Contact</p>
            <a href="tel:+919353401156" className="font-mono text-xs text-zinc-300 flex items-center gap-2 hover:text-orange-500"><Phone className="w-3 h-3 text-orange-500" />+91 93534 01156</a>
            <a href="mailto:hello@macjit.com" className="font-mono text-xs text-zinc-300 flex items-center gap-2 hover:text-orange-500 mt-1"><Mail className="w-3 h-3 text-orange-500" />hello@macjit.com</a>
            <p className="font-mono text-xs text-zinc-500 mt-2">8:00 AM — 6:00 PM</p>
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-600 mb-3">Quick links</p>
            <a href="#enquiry" className="block font-mono text-xs text-zinc-400 hover:text-orange-500 mt-1">Request trial</a>
            <button
              type="button"
              data-testid="footer-terminal-link"
              onClick={goStaff}
              className="block font-mono text-xs text-zinc-400 hover:text-orange-500 mt-1 text-left"
            >
              Terminal
            </button>
          </div>
        </div>
        <div className="border-t border-zinc-900 py-4 flex flex-wrap items-center justify-between gap-2 max-w-7xl mx-auto px-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-600">© 2026 MacJit · macjit.com · All rights reserved.</p>
        </div>
      </footer>
      <SupportDock
        open={supportOpen}
        setOpen={setSupportOpen}
        support={support}
        setSupport={setSupport}
        conversation={supportConversation}
        text={supportText}
        setText={setSupportText}
        busy={supportBusy}
        onSubmit={submitSupport}
      />
      <CheckoutModal plan={checkoutPlan} busy={checkoutBusy} onClose={() => setCheckoutPlan(null)} onSubmit={submitCheckout} />
      <MobileLoginModal login={loginModal} onClose={() => setLoginModal(null)} />
    </div>
  );
}

function MobileLoginModal({ login, onClose }) {
  if (!login) return null;
  const product = login.product === "mini" ? "mini" : "terminal";
  return (
    <div className="fixed inset-0 z-[80] bg-black/70 p-3 backdrop-blur-md md:hidden">
      <div className="mx-auto flex max-h-[92dvh] max-w-md flex-col overflow-hidden border border-orange-500/50 bg-zinc-950 shadow-2xl shadow-black/70">
        <div className="flex items-center justify-between gap-3 border-b border-zinc-800 bg-black px-4 py-3">
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.28em] text-orange-500">Secure access</p>
            <p className="font-display text-xl font-black uppercase tracking-tight text-white">{login.title}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close login"
            className="grid h-10 w-10 place-items-center border border-zinc-800 text-zinc-400 hover:border-orange-500 hover:text-orange-500"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <CompactProductLogin product={product} />
      </div>
    </div>
  );
}

function CompactProductLogin({ product }) {
  const { login } = useAuth();
  const nav = useNavigate();
  const isMini = product === "mini";
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const formatIdentifier = (value) => {
    const trimmed = String(value || "").trim();
    if (!trimmed) return "";
    if (trimmed.includes("@") || /[a-zA-Z]/.test(trimmed)) return trimmed.toLowerCase();
    const digits = trimmed.replace(/\D/g, "");
    if (!digits) return "";
    if (digits.startsWith("91") && digits.length > 10) return `+${digits.slice(0, 12)}`;
    return `+91${digits.slice(0, 10)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!identifier.trim() || !password) return toast.error(`Enter ${isMini ? "Mini" : "Terminal"} login and password`);
    setBusy(true);
    try {
      const { user, mustReset } = await login(identifier.trim(), password);
      if (mustReset) {
        toast.message("Set a new password to continue");
        nav(isMini ? "/mini" : "/terminal");
        return;
      }
      toast.success(`Welcome, ${user.name}`);
      nav(moduleHome(user) || roleHomePath(user.role));
    } catch (err) {
      toast.error(err.response?.data?.detail || `${isMini ? "Mini" : "Terminal"} login failed`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="overflow-y-auto bg-zinc-950 p-5">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">
            {isMini ? "Mini sign in" : "Terminal sign in"}
          </p>
          <h2 className="mt-2 font-display text-3xl font-black uppercase tracking-tight text-white">
            {isMini ? "Merchant desk" : "Crew access"}
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-zinc-500">
            {isMini ? "For Mini owners and counter billing access." : "For garage owner, admin, and staff access."}
          </p>
        </div>
        <div className="grid h-12 w-12 shrink-0 place-items-center bg-orange-500 text-black">
          {isMini ? <Calculator className="h-6 w-6" /> : <KeyRound className="h-6 w-6" />}
        </div>
      </div>

      <form onSubmit={submit} className="space-y-4">
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
            {isMini ? "Registered mobile or email" : "Registered mobile / platform email"}
          </span>
          <div className="relative mt-2">
            {isMini ? (
              <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
            ) : (
              <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
            )}
            <input
              type="text"
              inputMode="text"
              autoComplete="username"
              spellCheck={false}
              required
              value={identifier}
              onChange={(e) => setIdentifier(formatIdentifier(e.target.value))}
              placeholder={isMini ? "+91... or owner@email.com" : "+91... or platform email"}
              className="w-full border border-zinc-800 bg-black py-3 pl-10 pr-4 font-mono text-white outline-none focus:border-orange-500"
            />
          </div>
        </label>
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Password</span>
          <div className="relative mt-2">
            <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={isMini ? "Mini password" : "Terminal password"}
              className="w-full border border-zinc-800 bg-black py-3 pl-10 pr-4 font-mono text-white outline-none focus:border-orange-500"
            />
          </div>
        </label>
        <button disabled={busy} className="flex w-full items-center justify-center gap-2 bg-orange-500 py-4 font-display text-sm font-black uppercase tracking-widest text-black hover:bg-orange-400 disabled:opacity-50">
          {busy ? "Opening..." : <>Open {isMini ? "Mini" : "Terminal"} <ArrowRight className="h-4 w-4" /></>}
        </button>
      </form>
    </div>
  );
}

function navigateInsideApp(url, nav) {
  if (!url || typeof window === "undefined") return false;
  try {
    const next = new URL(url, window.location.origin);
    if (next.origin !== window.location.origin) return false;
    nav(`${next.pathname}${next.search}${next.hash}`);
    return true;
  } catch {
    if (url.startsWith("/")) {
      nav(url);
      return true;
    }
    return false;
  }
}

function HeroModuleCarousel({ slides, active, setActive, compact = false }) {
  const slide = slides[active] || slides[0];

  return (
    <div className="relative overflow-hidden border border-zinc-800 bg-zinc-950 shadow-[0_24px_70px_rgba(0,0,0,0.55)]">
      <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(249,115,22,0.16),transparent_28%),linear-gradient(180deg,rgba(255,255,255,0.04),transparent_40%)]" />
      <div className="absolute inset-0 opacity-[0.12] bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.8)_1px,transparent_0)] [background-size:18px_18px]" />
      <div className="absolute inset-0 scanline" />
      <div className="relative p-3 sm:p-4">
        <div className="mb-3 flex items-center justify-between gap-3 border border-zinc-800 bg-black/70 px-3 py-2 backdrop-blur">
          <div className="min-w-0">
            <p className="font-mono text-[8px] uppercase tracking-[0.28em] text-orange-500">{slide.eyebrow}</p>
            <p className="truncate font-display text-lg font-black uppercase tracking-tight text-white">{slide.title}</p>
          </div>
          <div className="hidden shrink-0 items-center gap-2 sm:flex">
            <span className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_12px_rgba(52,211,153,0.85)]" />
            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">Sanitized preview</span>
          </div>
        </div>

        <div className={`relative overflow-hidden border border-zinc-800 bg-black ${compact ? "h-[300px]" : "h-[430px] sm:h-[460px] lg:h-[500px]"}`}>
          <div className="absolute left-0 top-0 h-full w-1 bg-orange-500" />
          <div className="absolute right-4 top-4 rounded-full border border-orange-500/50 bg-orange-500/10 px-3 py-1 font-mono text-[8px] uppercase tracking-[0.22em] text-orange-400">
            Live module
          </div>
          <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-orange-500/12 to-transparent" />
          <div className={`relative h-full overflow-hidden ${compact ? "scale-[0.72] origin-top-left w-[138.888%] p-3 pt-12" : "p-3 pt-12 sm:p-4 sm:pt-12"}`}>
            {slide.render === "mechanic" && <MechanicModulePreview />}
            {slide.render === "reception" && <ReceptionModulePreview />}
            {slide.render === "tracker" && <TrackerModulePreview />}
            {slide.render === "communication" && <CommunicationModulePreview />}
          </div>
        </div>

        <div className="mt-4 grid min-h-[42px] gap-3 sm:grid-cols-[1fr_auto] sm:items-center">
          <p className="min-w-0 truncate whitespace-nowrap font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">{slide.meta}</p>
          <div className="flex h-3 items-center gap-2">
            {slides.map((item, index) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setActive(index)}
                aria-label={`Show ${item.title}`}
                className={`h-2.5 transition-all ${index === active ? "w-8 bg-orange-500" : "w-2.5 bg-zinc-700 hover:bg-zinc-500"}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function MechanicModulePreview() {
  return (
    <div className="grid h-full gap-4 lg:grid-cols-[0.75fr_1.25fr]">
      <div className="space-y-3">
        <p className="font-mono text-[8px] uppercase tracking-[0.28em] text-zinc-500">My queue (1)</p>
        <div className="border border-orange-500 bg-orange-500/20 p-3">
          <p className="font-display text-xl font-black uppercase tracking-tight text-white blur-[2px]">KA01KG2222</p>
          <p className="font-mono text-[9px] uppercase tracking-widest text-zinc-300">Masked plate</p>
          <p className="mt-2 font-mono text-[8px] uppercase tracking-[0.2em] text-orange-300">In service</p>
        </div>
      </div>
      <div className="space-y-4">
        <div className="border border-zinc-800 bg-zinc-950 p-4">
          <p className="font-mono text-[8px] uppercase tracking-[0.28em] text-zinc-500">Active job</p>
          <div className="mt-2 flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="font-display text-3xl font-black uppercase leading-none text-white">KA••••2222</p>
              <p className="mt-1 font-mono text-[10px] text-zinc-400">Customer name hidden</p>
              <p className="font-mono text-[9px] text-zinc-600">+91 ••••• 01156 - general</p>
            </div>
            <span className="border border-orange-500 px-3 py-1 font-mono text-[8px] uppercase tracking-[0.2em] text-orange-400">In service</span>
          </div>
          <button className="mt-5 w-full border border-zinc-700 bg-black py-4 font-display text-sm font-black uppercase tracking-[0.35em] text-white">
            Finish - send to QA
          </button>
        </div>
        <div className="border border-zinc-800 bg-zinc-950 p-4">
          <div className="flex items-center justify-between">
            <p className="font-mono text-[8px] uppercase tracking-[0.28em] text-zinc-500">Parts cart - Rs 0</p>
            <span className="border border-orange-500/60 px-2 py-1 font-mono text-[8px] uppercase tracking-widest text-orange-400">Heavy work</span>
          </div>
          <div className="mt-4 border border-zinc-800 px-3 py-2 font-mono text-[10px] text-zinc-500">Search parts: oil, brake, filter...</div>
          {["Air Filter", "Brake Pads Front", "Chain Sprocket Kit"].map((item) => (
            <div key={item} className="mt-2 flex items-center justify-between border border-zinc-800 bg-black px-3 py-2">
              <div>
                <p className="font-display text-sm font-bold text-white">{item}</p>
                <p className="font-mono text-[8px] uppercase text-zinc-600">SKU hidden - stock synced</p>
              </div>
              <span className="grid h-8 w-8 place-items-center bg-orange-500 font-display font-black text-black">+</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ReceptionModulePreview() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-4 gap-px border border-zinc-800 bg-zinc-800">
        {[
          ["Today serviced", "12"],
          ["Active bays", "04"],
          ["Payment due", "03"],
          ["QA before", "01"],
        ].map(([label, value], index) => (
          <div key={label} className={`bg-zinc-950 p-3 ${index === 0 ? "border-b-2 border-orange-500" : ""}`}>
            <p className="font-mono text-[7px] uppercase tracking-[0.24em] text-zinc-600">{label}</p>
            <p className="mt-2 font-display text-2xl font-black text-white">{value}</p>
          </div>
        ))}
      </div>
      <div className="grid gap-4 lg:grid-cols-[0.72fr_1.28fr]">
        <div className="border border-zinc-800 bg-zinc-950 p-3">
          <p className="font-mono text-[8px] uppercase tracking-[0.28em] text-orange-500">Walk-in</p>
          <p className="mt-1 font-display text-lg font-black uppercase text-white">New booking</p>
          {["Customer name", "+91 phone", "Email for OTP", "Plate", "Service menu"].map((field) => (
            <div key={field} className="mt-2 border border-zinc-800 bg-black px-3 py-2 font-mono text-[9px] text-zinc-500">{field}</div>
          ))}
          <button className="mt-3 w-full bg-orange-500 py-3 font-display text-xs font-black uppercase tracking-widest text-black">
            Create + auto-assign
          </button>
        </div>
        <div className="border border-zinc-800 bg-zinc-950 p-3">
          <p className="font-mono text-[8px] uppercase tracking-[0.28em] text-orange-500">Reception command center</p>
          <p className="mt-1 font-display text-xl font-black text-white">Live Service Desk</p>
          <div className="mt-3 border border-zinc-800 bg-black px-3 py-2 font-mono text-[9px] text-zinc-500">Search phone, plate, customer...</div>
          {[
            ["KA••••2335", "Cancelled"],
            ["KA••••2222", "Assigned"],
            ["KA••••1111", "Billed"],
          ].map(([plate, status]) => (
            <div key={plate} className="mt-3 border border-zinc-800 bg-black/70 p-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-display text-lg font-black uppercase text-white">{plate}</p>
                  <p className="font-mono text-[8px] text-zinc-600">Customer and mobile masked</p>
                </div>
                <span className="border border-orange-500/50 px-2 py-1 font-mono text-[8px] uppercase tracking-widest text-orange-400">{status}</span>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {["Details", "Bill", "Pay link"].map((action) => (
                  <span key={action} className="border border-zinc-700 px-2 py-1 font-mono text-[8px] uppercase tracking-widest text-zinc-400">{action}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function TrackerModulePreview() {
  return (
    <div className="mx-auto max-w-[330px] border border-zinc-300 bg-zinc-100 p-4 text-black shadow-[18px_18px_0_rgba(249,115,22,0.18)]">
      <p className="font-mono text-[8px] uppercase tracking-[0.26em] text-zinc-500">Track your service</p>
      <p className="mt-1 font-display text-3xl font-black tracking-tight">CarTracker</p>
      <div className="mt-4 flex border border-zinc-300 bg-white p-2">
        <span className="flex-1 font-mono text-[8px] uppercase tracking-widest text-zinc-400">Optional plate filter</span>
        <span className="bg-orange-500 px-3 py-1 font-display text-[9px] font-black uppercase">Show cars</span>
      </div>
      <div className="mt-4 border border-zinc-300 bg-white p-3">
        <div className="flex items-start justify-between">
          <div>
            <p className="font-mono text-[7px] uppercase tracking-[0.24em] text-zinc-400">Booking for</p>
            <p className="font-display text-xl font-black">Test customer</p>
            <p className="font-mono text-[8px] text-zinc-500">KA••••2222 - general</p>
          </div>
          <span className="rounded-full border border-cyan-400 px-2 py-1 font-mono text-[7px] uppercase tracking-widest text-cyan-600">Assigned</span>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3 border-t border-zinc-200 pt-3">
          <div>
            <p className="font-mono text-[7px] uppercase tracking-widest text-zinc-400">Mechanic</p>
            <p className="font-display font-bold">Pr••••••</p>
          </div>
          <div>
            <p className="font-mono text-[7px] uppercase tracking-widest text-zinc-400">Mobile</p>
            <p className="font-mono text-[9px] blur-[2px]">9353401156</p>
          </div>
        </div>
      </div>
      <div className="mt-4 border border-zinc-900 bg-white p-3">
        <p className="font-mono text-[8px] uppercase tracking-[0.24em] text-orange-500">Car location now</p>
        <p className="mt-1 font-display text-xl font-black">Assigned service bay</p>
      </div>
      <div className="mt-4 border border-zinc-300 bg-white p-3">
        <p className="font-mono text-[8px] uppercase tracking-[0.24em] text-zinc-400">Service progress</p>
        {["Booked", "Assigned", "In service", "Ready to test", "Billed", "Paid"].map((step, index) => (
          <div key={step} className="mt-2 flex items-center gap-2">
            <span className={`h-2.5 w-2.5 rounded-full ${index < 2 ? "bg-emerald-500" : "bg-zinc-300"}`} />
            <span className="font-mono text-[8px] uppercase tracking-widest text-zinc-700">{step}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function CommunicationModulePreview() {
  return (
    <div className="mx-auto flex h-[410px] max-w-[360px] flex-col overflow-hidden rounded-[26px] border border-orange-500 bg-zinc-950 shadow-[0_20px_70px_rgba(249,115,22,0.2)]">
      <div className="flex items-center justify-between border-b border-zinc-800 bg-zinc-950 p-4">
        <div className="flex items-center gap-3">
          <span className="font-display text-2xl font-black text-orange-500">∞</span>
          <div>
            <p className="font-display text-lg font-black uppercase tracking-[0.18em] text-white">Team chat</p>
            <p className="font-mono text-[8px] uppercase tracking-[0.24em] text-zinc-500">Brand hidden for tenant safety</p>
          </div>
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
        </div>
        <span className="font-display text-xl text-white">×</span>
      </div>
      <div className="border-b border-zinc-800 bg-zinc-900/80 p-4">
        <div className="rounded-full border border-zinc-700 px-4 py-3 font-display text-sm font-black text-white">
          All staff
        </div>
      </div>
      <div className="flex-1 space-y-4 overflow-hidden bg-black p-4">
        {[
          ["Reception", "Private invoice shared", "inventory-sample...xlsx"],
          ["Mechanic", "QA note updated", "masked-service-card.pdf"],
        ].map(([role, text, file]) => (
          <div key={role} className="ml-auto w-[82%] rounded-2xl border border-orange-500/70 bg-orange-950/45 p-4">
            <p className="font-mono text-[9px] uppercase tracking-[0.24em] text-zinc-300">You - {role}</p>
            <p className="mt-2 font-display text-lg font-bold text-white">{text}</p>
            <div className="mt-3 flex items-center gap-2 overflow-hidden rounded-xl border border-orange-500/40 bg-black/40 px-3 py-2">
              <PackageCheck className="h-4 w-4 shrink-0 text-orange-500" />
              <span className="truncate font-display text-sm font-bold text-white">{file}</span>
              <span className="ml-auto shrink-0 font-mono text-[9px] text-zinc-400">11 KB</span>
            </div>
          </div>
        ))}
      </div>
      <div className="border-t border-zinc-800 bg-zinc-950 p-4">
        <div className="flex items-center gap-3 rounded-2xl border border-zinc-800 bg-black px-4 py-3 text-zinc-500">
          <MessageCircle className="h-4 w-4 text-orange-500" />
          <span className="font-display text-sm font-bold">Message all staff...</span>
          <span className="ml-auto grid h-9 w-9 place-items-center rounded-full bg-orange-500/50 text-orange-100">
            <Send className="h-4 w-4" />
          </span>
        </div>
      </div>
    </div>
  );
}

function ProductPlatformSection({ onCheckout, onOpenLogin }) {
  return (
    <section id="product" className="bg-black py-16 sm:py-20 lg:py-24 reveal-up">
      <div className="mx-auto flex max-w-7xl flex-col px-5 sm:px-6">
        <div className="order-1 grid gap-8 lg:grid-cols-[0.85fr_1.15fr] lg:items-end">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">02 — Terminal</p>
            <h2 className="mt-3 font-display text-4xl font-black uppercase leading-[0.92] tracking-tighter sm:text-5xl lg:text-7xl">
              Run every garage like a live control room.
            </h2>
            <p className="mt-5 max-w-2xl text-sm leading-relaxed text-zinc-400 sm:mt-6">
              MacJit gives garage owners service booking, customer tracking, parts booking, staff coordination, billing, inventory, secure internal communication, subscriptions, and multi-branch control in one product.
            </p>
            <div className="mt-7 grid max-w-lg grid-cols-2 gap-3 sm:mt-9">
              <button type="button" onClick={() => onOpenLogin("mini")} className="border border-orange-500/70 bg-orange-500/10 px-5 py-4 text-center font-display text-sm font-black uppercase tracking-widest text-orange-300 hover:bg-orange-500 hover:text-black">
                Mini
              </button>
              <button type="button" onClick={() => onOpenLogin("terminal")} className="border border-zinc-700 px-5 py-4 text-center font-display text-sm font-black uppercase tracking-widest text-zinc-100 hover:border-orange-500 hover:text-orange-500">
                Terminal
              </button>
            </div>
          </div>
          <ProductSpeedometer />
        </div>

        <div id="plans" className="order-2 mt-12 rounded-none border-y border-orange-500/30 bg-orange-500/[0.03] py-10 lg:order-4 lg:mt-20 lg:border-y-0 lg:bg-transparent lg:py-0">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Plans</p>
              <h3 className="mt-3 max-w-3xl font-display text-3xl font-black uppercase leading-none tracking-tighter sm:text-4xl lg:text-5xl">
                <span className="lg:hidden">Pick your launch plan.</span>
                <span className="hidden lg:inline">Choose your Terminal stack.</span>
              </h3>
              <p className="mt-4 max-w-2xl text-sm leading-relaxed text-zinc-400">
                Mini starts simple billing fast. Terminal Pro unlocks the full garage workflow with service, shop, staff, payment, storage, and customer tracking control.
              </p>
            </div>
          </div>
          <div className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {PRODUCT_PLANS.map((plan) => {
              const available = plan.available !== false;
              return (
              <div id={`plan-${plan.key}`} key={plan.key} className={`relative flex min-h-full scroll-mt-24 flex-col border p-5 transition-colors ${available ? "border-zinc-800 bg-zinc-950 hover:border-orange-500/70" : "border-zinc-800/70 bg-zinc-950/50 opacity-70"}`}>
                {!available && (
                  <div className="absolute inset-0 z-10 grid place-items-center bg-black/35 backdrop-blur-[1px]">
                    <div className="border border-zinc-700/70 bg-zinc-950/70 px-5 py-4 text-center">
                      <Lock className="mx-auto h-8 w-8 text-zinc-400/60" />
                      <p className="mt-3 font-display text-lg font-black uppercase tracking-tight text-white/80">Launching soon</p>
                      <p className="mt-1 max-w-48 text-xs leading-relaxed text-zinc-400">We are polishing this stack for a stronger release.</p>
                    </div>
                  </div>
                )}
                <div className="min-h-[148px]">
                  <p className="font-display text-2xl font-black uppercase tracking-tight">{plan.name}</p>
                  <p className="mt-2 font-display text-3xl font-black text-orange-500">{plan.price}</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-orange-300">{plan.offer}</p>
                  <p className="mt-3 text-xs font-mono uppercase tracking-[0.16em] text-zinc-500">{plan.meta}</p>
                </div>
                <div className="mt-5 border-y border-zinc-800 py-4">
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-600">Best for</p>
                  <p className="mt-2 text-sm leading-relaxed text-zinc-300">{plan.bestFor}</p>
                </div>
                <div className="mt-6 space-y-3">
                  {plan.points.map((point) => (
                    <p key={point} className="flex items-center gap-2 text-sm text-zinc-300">
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-orange-500" /> {point}
                    </p>
                  ))}
                </div>
                {plan.loop && (
                  <p className="mt-5 border border-zinc-800 bg-black px-3 py-2 font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-400">
                    {plan.loop}
                  </p>
                )}
                {available ? (
                  <div className="mt-6 grid grid-cols-2 gap-2 pt-2">
                    <button type="button" onClick={() => onCheckout(plan, "trial")} className="flex items-center justify-center gap-2 border border-orange-500 px-4 py-3 font-display text-xs font-black uppercase tracking-widest text-orange-300 hover:bg-orange-500 hover:text-black">
                      Try it
                    </button>
                    <button type="button" onClick={() => onCheckout(plan, "paid")} className="flex items-center justify-center gap-2 bg-orange-500 px-4 py-3 font-display text-xs font-black uppercase tracking-widest text-black hover:bg-orange-400">
                      Subscribe <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <button type="button" disabled className="mt-auto flex cursor-not-allowed items-center justify-center gap-2 border border-zinc-700 px-4 py-3 font-display text-xs font-black uppercase tracking-widest text-zinc-500">
                    <Lock className="h-4 w-4 opacity-50" /> Coming Soon
                  </button>
                )}
              </div>
              );
            })}
          </div>
          <p className="mt-6 max-w-2xl text-sm leading-relaxed text-zinc-400">
            Every plan includes tenant isolation, owner login, staff limit checks, subscription status control, email-ready onboarding, and upgrade paths. Platform owner activates the final account after payment or approved trial.
          </p>
        </div>

        <div id="product-previews" className="order-3 mt-14 grid gap-8 lg:order-6 lg:mt-20 lg:grid-cols-[0.8fr_1.2fr] lg:items-end">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Live product previews</p>
            <h3 className="mt-3 font-display text-4xl lg:text-5xl font-black uppercase leading-none tracking-tighter">Show the power. Hide the secrets.</h3>
            <p className="mt-5 text-sm leading-relaxed text-zinc-400">
              Visitors see enough to trust the product: customer tracking, admin management, billing, parts, service flow, and internal communication. Sensitive records stay masked and blurred.
            </p>
          </div>
          <ProductPreviewWall />
        </div>

        <div className="order-4 mt-14 lg:order-3 lg:mt-20">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">What the product does</p>
          <h3 className="mt-3 max-w-3xl font-display text-4xl lg:text-5xl font-black uppercase leading-none tracking-tighter">One system for owner, staff, and customer.</h3>
          <div className="mt-10 grid gap-px bg-zinc-800 md:grid-cols-3">
            <ProductFeature icon={Clock} title="Trial to paid" text="Onboard Mini with 99 free transactions, plan limits, subscription status, and access blocking." />
            <ProductFeature icon={ShieldCheck} title="Secure customer page" text="Customers verify email OTP, track car status, approve work, receive bills, and pay." />
            <ProductFeature icon={Sparkles} title="Tenant website" text="Each garage gets a customer-facing page for service requests, tracking, and parts booking." />
          </div>
          <div className="mt-10 grid gap-px bg-zinc-800 lg:grid-cols-4">
            <ProductWorkflow icon={CreditCard} title="Smooth payments" metric="Link sent" text="Generate bills, send payment links, collect cash or online payment, and keep invoice history clean." />
            <ProductWorkflow icon={CalendarCheck} title="Daily control" metric="Queue clear" text="Bookings, staff handoff, service status, and owner alerts stay visible without repeated phone calls." />
            <ProductWorkflow icon={PackageCheck} title="Parts flow" metric="Stock locked" text="Customers can request parts while staff confirm availability and reduce duplicate bookings." />
            <ProductWorkflow icon={MessageCircle} title="Internal comms" metric="Live now" text="A secured chat layer keeps reception, shop, mechanic, tester, and admin aligned." />
          </div>
        </div>
      </div>
    </section>
  );
}

function ProductSpeedometer() {
  const ticks = [
    { label: "Book", className: "left-[7%] bottom-[52px]" },
    { label: "Assign", className: "left-[24%] bottom-[118px]" },
    { label: "Bill", className: "right-[24%] bottom-[118px]" },
    { label: "Paid", className: "right-[6%] bottom-[52px]" },
  ];

  return (
    <div className="overflow-hidden border border-zinc-800 bg-zinc-950/80 p-5 shadow-2xl shadow-black/40 backdrop-blur">
      <div className="flex items-center justify-between">
        <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-zinc-500">Ops acceleration</p>
        <Activity className="h-4 w-4 text-orange-500" />
      </div>
      <div className="relative mx-auto mt-5 h-44 w-72 max-w-full">
        <div className="absolute inset-x-4 bottom-0 h-36 rounded-t-full border-x border-t border-zinc-700 bg-black" />
        <div className="absolute inset-x-10 bottom-0 h-28 rounded-t-full border-x border-t border-orange-500/60" />
        <div className="absolute bottom-0 left-1/2 h-2 w-2 -translate-x-1/2 rounded-full bg-orange-500" />
        <div className="speedometer-needle absolute bottom-1 left-1/2 h-1 w-28 origin-left rounded-full bg-orange-500 shadow-[0_0_24px_rgba(249,115,22,0.8)]">
          <span className="absolute -right-1 -top-1.5 h-4 w-4 rounded-full border border-orange-300 bg-orange-500 shadow-[0_0_18px_rgba(249,115,22,0.95)]" />
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-center">
          <p className="font-display text-5xl font-black tracking-tighter text-white">98%</p>
          <p className="font-mono text-[10px] uppercase tracking-widest text-orange-500">workflow speed</p>
        </div>
        {ticks.map((item) => (
          <span
            key={item.label}
            className={`absolute font-mono text-[9px] uppercase tracking-widest text-zinc-500 ${item.className}`}
          >
            {item.label}
          </span>
        ))}
      </div>
      <div className="mt-5 grid grid-cols-3 gap-px bg-zinc-800">
        <ProductSpeedStat label="Avg bill" value="2m" />
        <ProductSpeedStat label="Assign" value="Auto" />
        <ProductSpeedStat label="Stress" value="Low" />
      </div>
    </div>
  );
}

function ProductSpeedStat({ label, value }) {
  return (
    <div className="bg-black px-3 py-2">
      <p className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">{label}</p>
      <p className="font-display text-xl font-black text-orange-500">{value}</p>
    </div>
  );
}

function ProductPreviewWall() {
  return (
    <div className="grid gap-3 md:grid-cols-2">
      <ProductScreenshotCard title="Customer tracking" icon={Gauge} tag="Email OTP secured">
        <div className="space-y-3">
          <div className="h-2 w-24 bg-orange-500" />
          <div className="grid grid-cols-4 gap-2">
            {["Booked", "Service", "QA", "Bill"].map((item, i) => (
              <div key={item} className={`h-14 border ${i < 3 ? "border-orange-500/60 bg-orange-500/10" : "border-zinc-700 bg-zinc-900"} p-2`}>
                <p className="font-mono text-[8px] uppercase text-zinc-400">{item}</p>
              </div>
            ))}
          </div>
          <div className="border border-zinc-700 bg-zinc-900 p-3 blur-[1px]">
            <p className="font-mono text-[10px] uppercase text-zinc-500">KA-**-**-2024 · Customer masked</p>
          </div>
        </div>
      </ProductScreenshotCard>
      <ProductScreenshotCard title="Admin management" icon={Building2} tag="Tenant scoped">
        <div className="grid grid-cols-2 gap-2">
          {["Revenue", "Bays", "Inventory", "Bookings"].map((item) => (
            <div key={item} className="border border-zinc-700 bg-zinc-900 p-3">
              <p className="font-mono text-[8px] uppercase text-zinc-500">{item}</p>
              <p className="mt-1 font-display text-2xl font-black text-white blur-[1px]">###</p>
            </div>
          ))}
        </div>
      </ProductScreenshotCard>
      <ProductScreenshotCard title="Staff coordination" icon={Users} tag="Less follow-up">
        <div className="space-y-2">
          {["Role access", "Task handoff", "Owner alert"].map((item, index) => (
            <div key={item} className="flex items-center justify-between border border-zinc-700 bg-zinc-900 px-3 py-2">
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-300">{item}</span>
              <span className={index === 0 ? "text-orange-500" : "text-emerald-400"}>{index === 0 ? "Active" : "Synced"}</span>
            </div>
          ))}
        </div>
      </ProductScreenshotCard>
      <ProductScreenshotCard title="Live communication" icon={MessageCircle} tag="Internal only">
        <div className="space-y-2">
          <p className="font-display text-xl font-black tracking-tight blur-sm">L••P</p>
          {["Reception to shop", "Mechanic update", "Admin approval"].map((item) => (
            <div key={item} className="rounded-none border border-zinc-700 bg-zinc-900 px-3 py-2">
              <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">{item}</p>
            </div>
          ))}
        </div>
      </ProductScreenshotCard>
    </div>
  );
}

function ProductScreenshotCard({ title, tag, icon: Icon, children }) {
  return (
    <div className="min-h-[230px] border border-zinc-800 bg-zinc-950 p-4">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-orange-500" />
          <p className="font-display font-black tracking-tight">{title}</p>
        </div>
        <span className="inline-flex items-center gap-1 border border-zinc-700 px-2 py-1 font-mono text-[8px] uppercase tracking-widest text-zinc-400">
          <Lock className="h-3 w-3" /> {tag}
        </span>
      </div>
      {children}
    </div>
  );
}

function ProductFeature({ icon: Icon, title, text }) {
  return (
    <div className="bg-zinc-950 p-6">
      <Icon className="mb-5 h-5 w-5 text-orange-500" />
      <h3 className="font-display text-2xl font-black tracking-tight">{title}</h3>
      <p className="mt-3 text-sm leading-relaxed text-zinc-400">{text}</p>
    </div>
  );
}

function ProductWorkflow({ icon: Icon, title, metric, text }) {
  return (
    <div className="bg-zinc-950 p-5">
      <div className="flex items-center justify-between gap-3">
        <Icon className="h-5 w-5 text-orange-500" />
        <span className="border border-zinc-700 px-2 py-1 font-mono text-[9px] uppercase tracking-widest text-zinc-400">{metric}</span>
      </div>
      <h3 className="mt-5 font-display text-2xl font-black tracking-tight">{title}</h3>
      <p className="mt-3 text-sm leading-relaxed text-zinc-400">{text}</p>
    </div>
  );
}

function SupportDock({ open, setOpen, support, setSupport, conversation, text, setText, busy, onSubmit }) {
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-50 flex items-center gap-2 border border-orange-500 bg-black px-4 py-3 font-display text-sm font-black uppercase tracking-widest text-orange-500 shadow-2xl shadow-black/50 hover:bg-orange-500 hover:text-black"
      >
        <MessageCircle className="h-4 w-4" /> Support
      </button>
    );
  }

  const messages = conversation?.messages || [];
  const closed = String(conversation?.status || "").toUpperCase() === "CLOSED";

  return (
    <div className="fixed bottom-4 right-4 z-50 w-[calc(100vw-2rem)] max-w-md overflow-hidden border border-orange-500/40 bg-zinc-950 shadow-2xl shadow-black/60">
      <div className="flex items-center justify-between border-b border-zinc-800 bg-black px-4 py-3">
        <div>
          <p className="font-display font-black uppercase tracking-tight text-white">Product support</p>
          <p className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">
            {conversation?.support_ref ? `${conversation.support_ref} · ` : ""}{conversation?.support_online ? "Live with platform owner" : "Offline enquiry fallback"}
          </p>
        </div>
        <button onClick={() => setOpen(false)} className="grid h-9 w-9 place-items-center border border-zinc-800 text-zinc-400 hover:border-orange-500 hover:text-orange-500">
          <X className="h-4 w-4" />
        </button>
      </div>

      {!conversation ? (
        <form onSubmit={onSubmit} className="space-y-3 p-4">
          <SupportInput label="Name" value={support.name} onChange={(value) => setSupport({ ...support, name: value })} />
          <SupportInput label="Email" type="email" value={support.email} onChange={(value) => setSupport({ ...support, email: value.toLowerCase() })} />
          <SupportInput label="Mobile" value={support.phone} onChange={(value) => setSupport({ ...support, phone: value })} />
          <label className="block">
            <span className="mb-1 block font-mono text-[9px] uppercase tracking-widest text-zinc-500">Query</span>
            <textarea
              value={support.message}
              onChange={(e) => setSupport({ ...support, message: e.target.value })}
              rows={3}
              className="w-full resize-none border border-zinc-800 bg-black px-3 py-2 text-sm text-white outline-none focus:border-orange-500"
              placeholder="Tell us what you need..."
            />
          </label>
          <button disabled={busy} className="flex w-full items-center justify-center gap-2 bg-orange-500 py-3 font-display font-black uppercase tracking-widest text-black hover:bg-orange-400 disabled:opacity-50">
            <Send className="h-4 w-4" /> {busy ? "Connecting..." : "Start Support Chat"}
          </button>
        </form>
      ) : (
        <div>
          <div className="max-h-80 space-y-3 overflow-y-auto p-4">
            {!conversation.support_online && (
              <div className="border border-zinc-800 bg-black px-3 py-2 text-xs leading-relaxed text-zinc-400">
                Platform owner is not online right now, so this has also been saved as an enquiry. You can continue the chat when support replies.
              </div>
            )}
            {closed && (
              <div className="border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs leading-relaxed text-emerald-200">
                This support chat is closed. We saved the contact details for future reference.
              </div>
            )}
            {messages.map((message, index) => (
              <div key={`${message.ts || index}-${index}`} className={`flex ${message.from === "visitor" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[82%] border px-3 py-2 text-sm ${message.from === "visitor" ? "border-orange-500/40 bg-orange-500/10 text-orange-50" : "border-zinc-800 bg-black text-zinc-200"}`}>
                  <p className="mb-1 font-mono text-[8px] uppercase tracking-widest text-zinc-500">{message.from === "visitor" ? "You" : "Support"}</p>
                  <p className="whitespace-pre-wrap break-words">{message.message || message.text}</p>
                </div>
              </div>
            ))}
          </div>
          <form onSubmit={onSubmit} className="flex gap-2 border-t border-zinc-800 p-3">
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              disabled={busy || closed}
              placeholder={closed ? "Chat closed" : "Type message..."}
              className="min-w-0 flex-1 border border-zinc-800 bg-black px-3 py-2 text-sm text-white outline-none focus:border-orange-500 disabled:opacity-50"
            />
            <button disabled={busy || closed || !text.trim()} className="bg-orange-500 px-4 font-display font-black uppercase tracking-widest text-black hover:bg-orange-400 disabled:opacity-50">
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

function SupportInput({ label, type = "text", value, onChange }) {
  return (
    <label className="block">
      <span className="mb-1 block font-mono text-[9px] uppercase tracking-widest text-zinc-500">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-zinc-800 bg-black px-3 py-2 text-sm text-white outline-none focus:border-orange-500"
      />
    </label>
  );
}

function CheckoutModal({ plan, busy, onClose, onSubmit }) {
  const [form, setForm] = useState({
    tenant_name: "",
    garage_name: "",
    city: "",
    area: "",
    pincode: "",
    address: "",
    owner_name: "",
    owner_email: "",
    owner_phone: "",
  });

  useEffect(() => {
    if (plan) setForm({ tenant_name: "", garage_name: "", city: "", area: "", pincode: "", address: "", owner_name: "", owner_email: "", owner_phone: "" });
  }, [plan]);

  if (!plan) return null;

  const trialMode = plan.checkoutMode === "trial";
  const phone = normalizeCheckoutPhone(form.owner_phone);
  const upd = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const submit = (e) => {
    e.preventDefault();
    if (!form.tenant_name.trim()) return toast.error("Company name is required");
    if (!form.city.trim()) return toast.error("City is required");
    if (!form.owner_name.trim()) return toast.error("Owner name is required");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.owner_email.trim())) return toast.error("Enter a valid owner email");
    if (!/^\+91[6-9]\d{9}$/.test(phone)) return toast.error("Enter a valid Indian mobile number");
    onSubmit({
      ...form,
      plan: plan.key,
      garage_name: form.garage_name || form.tenant_name,
      owner_email: form.owner_email.trim().toLowerCase(),
      owner_phone: phone,
      mode: trialMode ? "trial" : "paid",
    });
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/75 px-4 backdrop-blur-sm">
      <div className="max-h-[92vh] w-full max-w-4xl overflow-y-auto border border-orange-500/50 bg-zinc-950 shadow-2xl shadow-orange-950/40">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-zinc-800 bg-zinc-950 px-5 py-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">{trialMode ? "Start free trial" : "Secure checkout"}</p>
            <p className="font-display text-2xl font-black uppercase tracking-tight">{plan.name} <span className="text-orange-500">{trialMode ? "Trial" : plan.price}</span></p>
          </div>
          <button onClick={onClose} className="grid h-10 w-10 place-items-center border border-zinc-800 text-zinc-400 hover:border-orange-500 hover:text-orange-500" aria-label="Close checkout">
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={submit} className="grid gap-6 p-5 lg:grid-cols-[1fr_320px]">
          <div className="grid gap-3 sm:grid-cols-2">
            <CheckoutInput label="Company name" value={form.tenant_name} onChange={(v) => upd("tenant_name", v)} required />
            <CheckoutInput label="Terminal / shop name" value={form.garage_name} onChange={(v) => upd("garage_name", v)} />
            <CheckoutInput label="City" value={form.city} onChange={(v) => upd("city", v)} required />
            <CheckoutInput label="Area" value={form.area} onChange={(v) => upd("area", v)} />
            <CheckoutInput label="Pincode" value={form.pincode} onChange={(v) => upd("pincode", v.replace(/\D/g, "").slice(0, 6))} />
            <CheckoutInput label="Owner name" value={form.owner_name} onChange={(v) => upd("owner_name", v)} required />
            <CheckoutInput label="Owner email" type="email" value={form.owner_email} onChange={(v) => upd("owner_email", v.toLowerCase())} required />
            <CheckoutInput label="Owner mobile" value={form.owner_phone} onChange={(v) => upd("owner_phone", v)} onBlur={() => upd("owner_phone", phone)} required />
            <label className="sm:col-span-2 block">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Address</span>
              <textarea value={form.address} onChange={(e) => upd("address", e.target.value)} rows={3} className="mt-2 w-full resize-none border border-zinc-800 bg-black px-3 py-3 font-mono text-sm text-white outline-none focus:border-orange-500" />
            </label>
          </div>
          <div className="border border-zinc-800 bg-black p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">You get</p>
            <p className="mt-3 font-display text-4xl font-black text-orange-500">{trialMode ? "Free" : plan.price}</p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-orange-300">{trialMode ? `Trial includes ${plan.offer}` : plan.offer}</p>
            <div className="mt-5 space-y-3">
              {plan.points.map((point) => (
                <p key={point} className="flex items-center gap-2 text-sm text-zinc-300"><CheckCircle2 className="h-4 w-4 shrink-0 text-orange-500" /> {point}</p>
              ))}
            </div>
            <div className="mt-6 border border-zinc-800 bg-zinc-950 px-3 py-3 text-xs leading-relaxed text-zinc-400">
              {trialMode
                ? "No payment is collected now. Terminal creates your tenant, first branch, owner login, trial subscription, and emails the temporary password automatically."
                : "Payment is collected first. After successful payment, Terminal creates your tenant, first branch, owner login, subscription, and emails the temporary password automatically."}
            </div>
            <button disabled={busy} className="mt-5 flex w-full items-center justify-center gap-2 bg-orange-500 py-3 font-display font-black uppercase tracking-widest text-black hover:bg-orange-400 disabled:opacity-50">
              <CreditCard className="h-4 w-4" /> {busy ? "Starting..." : trialMode ? "Start Trial" : "Pay & Activate"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CheckoutInput({ label, value, onChange, onBlur, type = "text", required = false }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</span>
      <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} onBlur={onBlur} placeholder={label} className="mt-2 w-full border border-zinc-800 bg-black px-3 py-3 font-mono text-sm text-white outline-none placeholder:text-zinc-700 focus:border-orange-500" />
    </label>
  );
}

function normalizeCheckoutPhone(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length === 10) return `+91${digits}`;
  if (digits.startsWith("91") && digits.length >= 12) return `+${digits.slice(0, 12)}`;
  return value || "";
}

function ServiceChatbot({ open, setOpen }) {
  const nav = useNavigate();
  const chatScrollRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [input, setInput] = useState("");
  const [stage, setStage] = useState("problem");
  const [pendingEditField, setPendingEditField] = useState("");
  const [messages, setMessages] = useState([INITIAL_TORQUE_MESSAGE]);
  const [details, setDetails] = useState({
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    car_make: "",
    plate_number: "",
    car_model: "",
    fuel_type: "",
    problem: "",
    service_type: "",
    preferred_drop_date: "",
    preferred_drop_time: "",
    additional_notes: "",
    known_service: false,
    preferred_slot: "",
    diagnosis_notes: {
      mechanic_feedback: "",
      duration: "",
      self_test: "",
    },
  });

  const readAccessLocation = () => {
    try {
      return JSON.parse(localStorage.getItem("macjit_access_location") || "{}");
    } catch {
      return {};
    }
  };

  const resetChat = useCallback(() => {
    setBusy(false);
    setInput("");
    setStage("problem");
    setMessages([INITIAL_TORQUE_MESSAGE]);
    setDetails({
      customer_name: "",
      customer_phone: "",
      customer_email: "",
      car_make: "",
      plate_number: "",
      car_model: "",
      fuel_type: "",
      problem: "",
      service_type: "",
      preferred_drop_date: "",
      preferred_drop_time: "",
      additional_notes: "",
      known_service: false,
      preferred_slot: "",
      diagnosis_notes: {
        mechanic_feedback: "",
        duration: "",
        self_test: "",
      },
    });
    setPendingEditField("");
  }, []);

  useEffect(() => {
    if (!open) return;
    const node = chatScrollRef.current;
    if (!node) return;
    node.scrollTop = node.scrollHeight;
  }, [messages, open, stage]);

  const formatPhone = (value) => {
    const trimmed = value.trim();
    if (!trimmed) return "";
    if (trimmed.startsWith("+")) return trimmed.replace(/[^\d+]/g, "");
    const digits = trimmed.replace(/\D/g, "");
    if (!digits) return "";
    if (digits.startsWith("91") && digits.length > 10) return `+${digits.slice(0, 12)}`;
    return `+91${digits.slice(0, 10)}`;
  };

  const isValidPhone = (value) => /^\+91[6-9]\d{9}$/.test(formatPhone(value || ""));
  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(value || "").trim());
  const yesWords = ["yes", "y", "confirm", "confirmed", "ok", "okay", "submit", "book", "proceed"];
  const noWords = ["no", "n", "change", "edit", "correct", "wait"];

  const prettyServiceName = (key) => {
    const names = {
      general: "General Service",
      "oil-change": "Oil & Filter Change",
      "full-service": "Full Service",
      alignment: "Wheel Alignment & Balancing",
      brake: "Brake Service",
      engine: "Engine Diagnosis",
      "diagnostic-check": "Diagnostic Check",
      "battery-replacement": "Battery Replacement",
      "tire-replacement": "Tire Replacement",
      "washing-detailing": "Washing & Detailing",
      "puncture-repair": "Puncture Repair",
    };
    return names[key] || String(key || "Custom service").replace(/-/g, " ");
  };

  const parseDropDate = (text) => {
    const v = String(text || "").trim().toLowerCase();
    const d = new Date();
    if (/\btomorrow\b/.test(v)) d.setDate(d.getDate() + 1);
    else if (/\btoday\b/.test(v)) {}
    else {
      const iso = v.match(/\b(20\d{2}-\d{2}-\d{2})\b/);
      if (iso) return iso[1];
      const slash = v.match(/\b(\d{1,2})[/-](\d{1,2})(?:[/-](\d{2,4}))?\b/);
      if (slash) {
        const day = slash[1].padStart(2, "0");
        const month = slash[2].padStart(2, "0");
        const year = slash[3] ? (slash[3].length === 2 ? `20${slash[3]}` : slash[3]) : String(new Date().getFullYear());
        return `${year}-${month}-${day}`;
      }
      return "";
    }
    return d.toISOString().slice(0, 10);
  };

  const parseDropTime = (text) => {
    const v = String(text || "").trim().toLowerCase();
    const match = v.match(/\b(\d{1,2})(?::(\d{2}))?\s*(am|pm)?\b/);
    if (!match) return "";
    let hour = Number(match[1]);
    const minute = Number(match[2] || 0);
    const meridian = match[3];
    if (meridian === "pm" && hour < 12) hour += 12;
    if (meridian === "am" && hour === 12) hour = 0;
    if (hour < 8 || hour > 18 || minute > 59) return "";
    return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
  };

  const preferredSlotIso = (d = details) => {
    if (!d.preferred_drop_date || !d.preferred_drop_time) return "";
    const local = new Date(`${d.preferred_drop_date}T${d.preferred_drop_time}:00`);
    return Number.isNaN(local.getTime()) ? "" : local.toISOString();
  };

  const mergeDetails = (base, updates = {}) => {
    const next = { ...base };
    if (updates.customer_name) next.customer_name = String(updates.customer_name).trim();
    if (updates.customer_phone) next.customer_phone = formatPhone(String(updates.customer_phone));
    if (updates.customer_email) next.customer_email = String(updates.customer_email).trim().toLowerCase();
    if (updates.car_make) next.car_make = String(updates.car_make).trim();
    if (updates.plate_number) {
      const rawPlate = String(updates.plate_number);
      next.plate_number = isValidIndianCarNumber(rawPlate) ? formatPlateForDisplay(rawPlate) : rawPlate.toUpperCase();
    }
    if (updates.car_model) next.car_model = String(updates.car_model).trim();
    if (updates.fuel_type) next.fuel_type = String(updates.fuel_type).trim();
    if (updates.problem) next.problem = String(updates.problem).trim();
    if (updates.additional_notes) next.additional_notes = String(updates.additional_notes).trim();
    if (updates.service_type) next.service_type = String(updates.service_type).trim();
    if (updates.preferred_drop_date) next.preferred_drop_date = String(updates.preferred_drop_date).trim();
    if (updates.preferred_drop_time) next.preferred_drop_time = String(updates.preferred_drop_time).trim();
    if (next.problem && !next.service_type) next.service_type = detectService(next.problem);
    next.known_service = Boolean(next.service_type);
    next.preferred_slot = preferredSlotIso(next);
    return next;
  };

  const extractLocalFields = (text) => {
    const value = String(text || "");
    const updates = {};
    const email = value.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
    if (email) updates.customer_email = email[0].toLowerCase();
    const phone = value.match(/(?:\+?91[-\s]?)?[6-9]\d{9}/);
    if (phone) updates.customer_phone = phone[0];
    const plateMatch = value.match(/\b([A-Z]{2}[-\s]?\d{1,2}[-\s]?[A-Z]{1,3}[-\s]?\d{4}|\d{2}[-\s]?BH[-\s]?\d{4}[-\s]?[A-Z]{1,2})\b/i);
    if (plateMatch) updates.plate_number = plateMatch[1];
    const date = parseDropDate(value);
    if (date) updates.preferred_drop_date = date;
    const time = parseDropTime(value);
    if (time) updates.preferred_drop_time = time;
    const fuel = value.match(/\b(petrol|diesel|cng|ev|electric|hybrid)\b/i);
    if (fuel) updates.fuel_type = fuel[1].toUpperCase();
    const makeModel = value.match(/\b(hyundai|maruti|suzuki|tata|mahindra|honda|toyota|kia|skoda|volkswagen|renault|nissan|ford|mg)\s+([a-z0-9][a-z0-9\s-]{1,30})/i);
    if (makeModel) {
      updates.car_make = makeModel[1];
      updates.car_model = makeModel[2].trim();
    }
    const service = detectService(value);
    if (service) updates.service_type = service;
    if (value.length > 8 && !email && !phone) updates.problem = value;
    return updates;
  };

  const missingField = (d) => {
    if (!d.customer_name) return "customer_name";
    if (!isValidPhone(d.customer_phone)) return "customer_phone";
    if (!isValidEmail(d.customer_email)) return "customer_email";
    if (!d.car_make) return "car_make";
    if (!d.car_model) return "car_model";
    if (!d.fuel_type) return "fuel_type";
    if (!d.problem && !d.service_type) return "problem";
    if (!d.preferred_drop_date) return "preferred_drop_date";
    if (!d.preferred_drop_time) return "preferred_drop_time";
    if (!d.additional_notes) return "additional_notes";
    return "";
  };

  const promptMissing = (field, d) => {
    if (field === "customer_name") return "Sure, I can help with that. May I have your full name?";
    if (field === "customer_phone") return "Please share your mobile number so our reception team can contact you if needed.";
    if (field === "customer_email") return "Please share your email address. I will send the booking confirmation there.";
    if (field === "car_make") return "Which car brand is it? For example Hyundai, Maruti, Tata, or Kia.";
    if (field === "car_model") return "And what is the model?";
    if (field === "fuel_type") return "What fuel type is it: Petrol, Diesel, CNG, Hybrid, or EV?";
    if (field === "problem") return "Tell me the main issue in your words, even a short line is fine.";
    if (field === "preferred_drop_date") return "Which date would you like to drop the car?";
    if (field === "preferred_drop_time") return "What time works for you between 8 AM and 6 PM?";
    if (field === "additional_notes") return `Any other issue we should know before inspection? If not, type "No additional issues".`;
    return `Thanks${d.customer_name ? `, ${d.customer_name}` : ""}. Please share one more detail.`;
  };

  const confirmationSummary = (d, custom = false) => {
    const drop = `${d.preferred_drop_date} at ${d.preferred_drop_time}`;
    return [
      custom ? "This needs manual inspection by our technical team. Please confirm these details:" : "I found a matching MacJit service. Please confirm these details:",
      `Name: ${d.customer_name}`,
      `Mobile: ${d.customer_phone}`,
      `Email: ${d.customer_email}`,
      `Car: ${d.car_make} ${d.car_model} (${d.fuel_type})`,
      `Registration: ${d.plate_number || "Not provided"}`,
      `Service: ${custom ? "Custom technical enquiry" : prettyServiceName(d.service_type)}`,
      `Drop: ${drop}`,
      `Notes: ${d.additional_notes || d.problem}`,
      "Reply YES to submit, or CHANGE to edit."
    ].join("\n");
  };

  const isGreeting = useCallback((text) => {
    const normalized = (text || "").toLowerCase().trim();
    if (!normalized) return false;
    const greetingWords = ["hi", "hello", "hey", "hii", "good morning", "good afternoon", "good evening", "namaste"];
    return greetingWords.some((word) => normalized === word || normalized.startsWith(`${word} `));
  }, []);

  const normalizePlate = (value) => value.toUpperCase().replace(/[\s-]+/g, "");

  const isValidIndianCarNumber = useCallback((plate) => {
    const normalized = normalizePlate(plate);
    const standardPattern = /^[A-Z]{2}\d{1,2}[A-Z]{1,3}\d{4}$/;
    const bhPattern = /^\d{2}BH\d{4}[A-Z]{1,2}$/;
    return standardPattern.test(normalized) || bhPattern.test(normalized);
  }, []);

  const formatPlateForDisplay = (plate) => {
    const normalized = normalizePlate(plate);
    const bhMatch = normalized.match(/^(\d{2})(BH)(\d{4})([A-Z]{1,2})$/);
    if (bhMatch) return `${bhMatch[1]} ${bhMatch[2]} ${bhMatch[3]} ${bhMatch[4]}`;
    const standardMatch = normalized.match(/^([A-Z]{2})(\d{1,2})([A-Z]{1,3})(\d{4})$/);
    if (standardMatch) return `${standardMatch[1]}-${standardMatch[2]}-${standardMatch[3]}-${standardMatch[4]}`;
    return normalized;
  };

  const detectService = useCallback((text) => {
    const p = (text || "").toLowerCase();
    if (p.includes("oil") || p.includes("filter")) return "oil-change";
    if (p.includes("brake")) return "brake";
    if (p.includes("battery")) return "battery-replacement";
    if (p.includes("engine") || p.includes("noise") || p.includes("start") || p.includes("click") || p.includes("accelerat") || p.includes("smoke") || p.includes("overheat")) return "engine";
    if (p.includes("full") || p.includes("complete") || p.includes("general service") || p.includes("service")) return "general";
    if (p.includes("wheel") || p.includes("align") || p.includes("balance")) return "alignment";
    return "";
  }, []);

  const detectEditField = useCallback((text) => {
    const normalized = (text || "").toLowerCase();
    const wantsUpdate = /wrong|update|change|correct|edit|mistake|typo/.test(normalized);
    if (!wantsUpdate) return "";
    if (/car number|vehicle number|plate|registration|rc/.test(normalized)) return "plate_number";
    if (/phone|mobile|contact/.test(normalized)) return "customer_phone";
    if (/name/.test(normalized)) return "customer_name";
    if (/model|car model/.test(normalized)) return "car_model";
    if (/issue|problem|noise|complaint/.test(normalized)) return "problem";
    return "";
  }, []);

  const promptForField = (field) => {
    if (field === "plate_number") return "No problem. Please send the correct car number (example: KA-05-MN-2024).";
    if (field === "customer_phone") return "Sure, please send the correct phone number.";
    if (field === "customer_name") return "Sure, please send the correct name.";
    if (field === "car_model") return "Sure, please send the correct car model.";
    if (field === "problem") return "Got it. Please tell me the correct issue in detail.";
    return "Tell me what you want to update.";
  };

  const push = (from, text) => setMessages((items) => [...items, { from, text }]);

  const submitBooking = async (finalDetails = details) => {
    setBusy(true);
    try {
      const slotIso = preferredSlotIso(finalDetails);
      const payload = {
        customer_name: finalDetails.customer_name,
        customer_phone: finalDetails.customer_phone,
        customer_email: finalDetails.customer_email,
        plate_number: finalDetails.plate_number,
        problem: `${finalDetails.problem || prettyServiceName(finalDetails.service_type)}\nAdditional notes: ${finalDetails.additional_notes || "None"}`,
        service_type: finalDetails.service_type,
        known_service: true,
        preferred_slot: slotIso,
        preferred_drop_date: finalDetails.preferred_drop_date,
        preferred_drop_time: finalDetails.preferred_drop_time,
        fuel_type: finalDetails.fuel_type,
        car_make: finalDetails.car_make,
        car_model: finalDetails.car_model,
        source: "torque-online-request",
        garage_presence_status: "NOT_IN_GARAGE",
      };
      const r = await api.post("/public/bookings", payload);
      push("bot", `Thank you, ${finalDetails.customer_name}. Your booking has been confirmed successfully.\n\nBooking ID: ${r.data.booking.id}\nService: ${prettyServiceName(finalDetails.service_type)}\nCar: ${finalDetails.car_make} ${finalDetails.car_model}\nDrop Time: ${finalDetails.preferred_drop_date} at ${finalDetails.preferred_drop_time}\n\nA confirmation email has also been sent to your registered email address. Please drop your car on or before the scheduled time.`);
      setStage("done");
      toast.success("Booking confirmed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not book this slot");
    } finally {
      setBusy(false);
    }
  };

  const submitCustomEnquiry = async (finalDetails = details) => {
    setBusy(true);
    try {
      await api.post("/enquiries", {
        name: finalDetails.customer_name,
        phone: finalDetails.customer_phone,
        email: finalDetails.customer_email,
        car_make: finalDetails.car_make,
        car_model: finalDetails.car_model,
        plate_number: finalDetails.plate_number,
        service_interest: "custom-service-manual-review",
        source: "torque-custom-enquiry",
        status: "Manual Inspection Required",
        message: [
          `Issue: ${finalDetails.problem || "Custom issue"}`,
          `Fuel type: ${finalDetails.fuel_type}`,
          `Preferred drop: ${finalDetails.preferred_drop_date} ${finalDetails.preferred_drop_time}`,
          `Additional notes: ${finalDetails.additional_notes || "None"}`,
        ].join("\n"),
      });
      push("bot", "Thank you for sharing the issue. This needs manual inspection, so our technical team will review it and contact you shortly.");
      setStage("done");
      toast.success("Custom enquiry created");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create enquiry");
    } finally {
      setBusy(false);
    }
  };

  const handleSend = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    push("user", text);

    const normalizedReply = text.toLowerCase().trim();
    if (stage === "clarify_issue") {
      const nextDetails = mergeDetails(details, { problem: `${details.problem || ""} ${text}`.trim(), ...extractLocalFields(text) });
      setDetails(nextDetails);
      setStage("problem");
      const missing = missingField(nextDetails);
      if (missing) {
        setPendingEditField(missing);
        push("bot", promptMissing(missing, nextDetails));
        return;
      }
      const custom = !nextDetails.service_type;
      setStage(custom ? "confirm_custom" : "confirm_booking");
      push("bot", confirmationSummary(nextDetails, custom));
      return;
    }

    if (stage === "confirm_booking" || stage === "confirm_custom") {
      if (yesWords.includes(normalizedReply)) {
        if (stage === "confirm_booking") await submitBooking(details);
        else await submitCustomEnquiry(details);
        return;
      }
      if (noWords.some((word) => normalizedReply.includes(word))) {
        setStage("problem");
        push("bot", "No problem. Tell me what you want to change.");
        return;
      }
      push("bot", "Please reply YES to submit, or CHANGE to edit the details.");
      return;
    }

    if (pendingEditField) {
      const local = extractLocalFields(text);
      let update = { ...local };
      if (pendingEditField === "plate_number") {
        if (!isValidIndianCarNumber(text) && !["skip", "optional", "no"].includes(normalizedReply)) {
          push("bot", "That still looks incorrect. Please send a valid car number like KA-05-MN-2024 or 21 BH 1234 AA.");
          return;
        }
        update.plate_number = ["skip", "optional", "no"].includes(normalizedReply) ? "Not provided" : formatPlateForDisplay(text);
      } else if (pendingEditField === "customer_phone") {
        const phone = formatPhone(text);
        if (!isValidPhone(phone)) {
          push("bot", "Please send a valid phone number with country code or 10 digits.");
          return;
        }
        update.customer_phone = phone;
      } else if (pendingEditField === "customer_email") {
        if (!isValidEmail(text)) {
          push("bot", "That email does not look valid. Please share a valid email address.");
          return;
        }
        update.customer_email = text.trim().toLowerCase();
      } else if (pendingEditField === "preferred_drop_date") {
        const date = parseDropDate(text);
        if (!date) {
          push("bot", "Please share a valid date, like tomorrow or 25/05/2026.");
          return;
        }
        update.preferred_drop_date = date;
      } else if (pendingEditField === "preferred_drop_time") {
        const time = parseDropTime(text);
        if (!time) {
          push("bot", "Please share a time between 8 AM and 6 PM, for example 11 AM.");
          return;
        }
        update.preferred_drop_time = time;
      } else if (pendingEditField === "additional_notes" && ["no", "none", "no additional issues", "nothing"].includes(normalizedReply)) {
        update.additional_notes = "No additional issues";
      } else {
        update[pendingEditField] = text;
      }
      const nextDetails = mergeDetails(details, update);
      setDetails(nextDetails);
      setPendingEditField("");
      const missing = missingField(nextDetails);
      if (missing) {
        setPendingEditField(missing);
        push("bot", promptMissing(missing, nextDetails));
        return;
      }
      const custom = !nextDetails.service_type;
      setStage(custom ? "confirm_custom" : "confirm_booking");
      push("bot", confirmationSummary(nextDetails, custom));
      return;
    }

    const fieldToEdit = detectEditField(text);
    if (fieldToEdit) {
      setPendingEditField(fieldToEdit);
      push("bot", promptForField(fieldToEdit));
      return;
    }

    try {
      const historyForAi = [...messages, { from: "user", text }];
      const localUpdates = extractLocalFields(text);
      const ai = await api.post("/public/torque-chat", {
        message: text,
        history: historyForAi,
        stage,
        details,
        access_location: readAccessLocation(),
      });
      const payload = ai.data || {};
      if (payload.service_available === false) {
        push("bot", payload.reply || "Sorry, MacJit service is not available in your city right now.");
        setStage("unavailable");
        return;
      }
      const updates = payload.field_updates || {};
      const nextDetails = mergeDetails(details, { ...updates, ...localUpdates });
      setDetails(nextDetails);

      if (payload.confidence === "MEDIUM" && !nextDetails.service_type && payload.clarification_question) {
        setStage("clarify_issue");
        push("bot", payload.clarification_question);
        return;
      }

      const missing = missingField(nextDetails);
      if (missing) {
        setPendingEditField(missing);
        push("bot", promptMissing(missing, nextDetails));
        return;
      }

      const custom = Boolean(payload.assign_to_admin) || !nextDetails.service_type;
      setStage(custom ? "confirm_custom" : "confirm_booking");
      push("bot", confirmationSummary(nextDetails, custom));
    } catch (err) {
      push("bot", "I could not process that now. Please repeat in short, I will help.");
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => {
          if (stage === "done") resetChat();
          setOpen(true);
        }}
        data-testid="macjit-chatbot-open"
        className="fixed bottom-5 right-5 z-40 bg-orange-500 hover:bg-orange-400 text-black shadow-2xl border-b-4 border-orange-700 active:translate-y-1 active:border-b-0 px-4 py-3 flex items-center gap-2.5"
      >
        <Wrench className="w-4 h-4" />
        <span className="font-display font-black uppercase tracking-[0.18em] text-xs">Ask Torque</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-3" data-testid="macjit-chatbot">
          <div className="w-full max-w-xl bg-zinc-950 border border-orange-500/40 shadow-2xl h-[min(88vh,680px)] flex flex-col rounded-sm">
            <div className="sticky top-0 bg-zinc-950 border-b border-zinc-800 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-500 text-black grid place-items-center">
                  <Wrench className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-display font-black uppercase tracking-tight text-white">Torque</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Problem reader / Slot booker</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={resetChat}
                  className="px-2.5 py-1 border border-zinc-800 text-[10px] font-mono uppercase tracking-wider text-zinc-300 hover:text-orange-500 hover:border-orange-500/40"
                >
                  New chat
                </button>
                <button onClick={() => setOpen(false)} className="w-9 h-9 border border-zinc-800 grid place-items-center text-zinc-400 hover:text-orange-500">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div ref={chatScrollRef} className="p-4 flex-1 min-h-0 overflow-y-auto space-y-3 bg-black">
              {messages.map((m, idx) => (
                <div key={idx} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed ${m.from === "user" ? "bg-orange-500 text-black" : "bg-zinc-900 text-zinc-100 border border-zinc-800"}`}>
                    <p className="font-mono whitespace-pre-wrap break-words">{m.text}</p>
                  </div>
                </div>
              ))}
              {stage === "done" && details.known_service && (
                <button
                  onClick={() => nav(`/track?plate=${encodeURIComponent(details.plate_number)}`)}
                  className="w-full bg-orange-500 hover:bg-orange-400 text-black font-display font-black uppercase tracking-widest px-5 py-3"
                >
                  Track car now
                </button>
              )}
            </div>
            <form onSubmit={handleSend} className="border-t border-zinc-800 p-3 flex gap-2 bg-zinc-950 shrink-0">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                disabled={busy}
                placeholder="Type your reply..."
                className="flex-1 bg-black border border-zinc-800 px-3 py-3 font-mono text-sm text-white focus:border-orange-500 outline-none disabled:opacity-50"
              />
              <button disabled={busy} className="bg-orange-500 hover:bg-orange-400 text-black font-display font-black uppercase tracking-widest px-5 disabled:opacity-50">
                Send
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

function EnquiryForm() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", garage_name: "", city: "", service_interest: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const formatPhone = (value) => {
    const trimmed = value.trim();
    if (!trimmed) return "";
    if (trimmed.startsWith("+")) return trimmed.replace(/[^\d+]/g, "");
    const digits = trimmed.replace(/\D/g, "");
    if (!digits) return "";
    if (digits.startsWith("91") && digits.length > 10) return `+${digits.slice(0, 12)}`;
    return `+91${digits.slice(0, 10)}`;
  };

  const isValidPhone = (value) => /^\+91[6-9]\d{9}$/.test(formatPhone(value));
  const isValidEmail = (value) => !value.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: k === "phone" ? formatPhone(v) : v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) return toast.error("Name, phone, and email are required");
    if (!form.service_interest) return toast.error("Choose a Terminal product plan");
    if (!isValidPhone(form.phone)) return toast.error("Enter a valid 10-digit Indian mobile number");
    if (!isValidEmail(form.email)) return toast.error("Enter a valid email address");
    setBusy(true);
    try {
      const planLabel = {
        terminal_ledger: "Mini",
        shop_inventory: "Shop Start",
        parts_seller: "Parts Seller",
        garage_core: "Garage Core",
        garage_shop: "Garage Shop",
        terminal_plus: "Terminal Plus",
      }[form.service_interest] || form.service_interest;
      const response = await api.post("/enquiries", {
        name: form.name.trim(),
        phone: formatPhone(form.phone),
        email: form.email.trim().toLowerCase(),
        service_interest: `Terminal ${planLabel}`,
        message: [
          form.garage_name ? `Garage / company: ${form.garage_name.trim()}` : "",
          form.city ? `City: ${form.city.trim()}` : "",
          form.message ? `Notes: ${form.message.trim()}` : "",
        ].filter(Boolean).join("\n") || `Terminal ${planLabel} trial request`,
        source: "terminal-platform-trial",
        status: "new",
      });
      toast.success(response.data?.id ? `Trial request received: ${response.data.id}` : "Trial request received");
      setDone(true);
      setForm({ name: "", phone: "", email: "", garage_name: "", city: "", service_interest: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send trial request");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div data-testid="enquiry-success" className="border border-orange-500/40 bg-orange-500/5 p-10 text-center">
        <Sparkles className="w-8 h-8 text-orange-500 mx-auto" />
        <p className="font-display font-black text-3xl uppercase tracking-tighter mt-4">Trial request received.</p>
        <p className="font-mono text-xs text-zinc-400 mt-3">We emailed your request confirmation. The platform owner will reach out soon.</p>
        <button onClick={() => setDone(false)} className="mt-6 font-mono text-[11px] uppercase tracking-widest text-orange-500 hover:text-orange-400">Request another</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="enquiry-form" className="border border-zinc-800 bg-zinc-900/40 p-6 lg:p-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
      <input data-testid="enq-name" required placeholder="Your name *" value={form.name} onChange={(e) => upd("name", e.target.value)} className="bg-zinc-950 border border-zinc-800 px-3 py-3 font-mono text-sm focus:border-orange-500 outline-none" />
      <input data-testid="enq-phone" required inputMode="tel" placeholder="Phone *" value={form.phone} onChange={(e) => upd("phone", e.target.value)} className={`bg-zinc-950 border px-3 py-3 font-mono text-sm focus:border-orange-500 outline-none ${form.phone && !isValidPhone(form.phone) ? "border-red-500/70" : "border-zinc-800"}`} />
      {form.phone && !isValidPhone(form.phone) && <p className="sm:col-span-2 font-mono text-[10px] uppercase tracking-widest text-red-400 -mt-1">Use a valid mobile number, for example 9876543210.</p>}
      <input data-testid="enq-email" required type="email" placeholder="Email *" value={form.email} onChange={(e) => upd("email", e.target.value)} className={`bg-zinc-950 border px-3 py-3 font-mono text-sm focus:border-orange-500 outline-none sm:col-span-2 ${form.email && !isValidEmail(form.email) ? "border-red-500/70" : "border-zinc-800"}`} />
      {form.email && !isValidEmail(form.email) && <p className="sm:col-span-2 font-mono text-[10px] uppercase tracking-widest text-red-400 -mt-1">Enter a valid email address.</p>}
      <input data-testid="enq-garage" placeholder="Garage / company name" value={form.garage_name} onChange={(e) => upd("garage_name", e.target.value)} className="bg-zinc-950 border border-zinc-800 px-3 py-3 font-mono text-sm focus:border-orange-500 outline-none" />
      <input data-testid="enq-city" placeholder="City" value={form.city} onChange={(e) => upd("city", e.target.value)} className="bg-zinc-950 border border-zinc-800 px-3 py-3 font-mono text-sm focus:border-orange-500 outline-none" />
      <select data-testid="enq-service" value={form.service_interest} onChange={(e) => upd("service_interest", e.target.value)} className="bg-zinc-950 border border-zinc-800 px-3 py-3 font-mono text-sm focus:border-orange-500 outline-none sm:col-span-2 text-zinc-300">
        <option value="">Choose product plan...</option>
        {PRODUCT_PLANS.map((plan) => (
          <option key={plan.key} value={plan.key} disabled={plan.available === false}>{plan.name} - {plan.available === false ? "Launching soon" : plan.price}</option>
        ))}
      </select>
      <textarea data-testid="enq-message" placeholder="Team size, branch count, or anything else..." value={form.message} onChange={(e) => upd("message", e.target.value)} rows={3} className="bg-zinc-950 border border-zinc-800 px-3 py-3 font-mono text-sm focus:border-orange-500 outline-none sm:col-span-2 resize-none" />
      <button type="submit" disabled={busy} data-testid="enq-submit" className="sm:col-span-2 bg-orange-500 hover:bg-orange-400 disabled:opacity-50 text-black font-display font-black uppercase tracking-widest py-3 transition-colors flex items-center justify-center gap-2">
        {busy ? "Sending..." : <><Send className="w-4 h-4" /> Request trial</>}
      </button>
    </form>
  );
}

function ProductInterestForm({ items = [] }) {
  const [form, setForm] = useState({ name: "", phone: "", product: "" });
  const [busy, setBusy] = useState(false);
  const selected = items.find((it) => it.id === form.product || it.name === form.product);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) return toast.error("Name and phone are required");
    setBusy(true);
    try {
      await api.post("/enquiries", {
        name: form.name,
        phone: form.phone,
        service_interest: "parts-request",
        message: `Customer requested inventory product: ${selected?.name || form.product || "Parts enquiry"}`,
        product_id: selected?.id || "",
        product_name: selected?.name || form.product,
        source: "landing-parts",
        status: "new",
      });
      toast.success("Request sent successfully. Our team will connect soon.");
      setForm({ name: "", phone: "", product: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send parts request");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-8 border border-zinc-800 bg-zinc-950/70 p-4 space-y-3">
      <div className="flex items-center gap-2 text-orange-500">
        <PackageCheck className="w-4 h-4" />
        <p className="font-mono text-[10px] uppercase tracking-[0.25em]">Request product booking</p>
      </div>
      <select
        value={form.product}
        onChange={(e) => setForm((f) => ({ ...f, product: e.target.value }))}
        className="w-full bg-black border border-zinc-800 px-3 py-3 font-mono text-sm text-zinc-300 focus:border-orange-500 outline-none"
      >
        <option value="">Select in-stock product...</option>
        {items.map((it) => (
          <option key={it.id || it.name} value={it.id || it.name}>{it.name} - Rs {it.price} ({it.stock} left)</option>
        ))}
      </select>
      <div className="grid sm:grid-cols-2 gap-2">
        <input required placeholder="Customer name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="bg-black border border-zinc-800 px-3 py-3 font-mono text-sm focus:border-orange-500 outline-none" />
        <input required placeholder="Phone number" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} className="bg-black border border-zinc-800 px-3 py-3 font-mono text-sm focus:border-orange-500 outline-none" />
      </div>
      <button disabled={busy} className="w-full bg-orange-500 hover:bg-orange-400 disabled:opacity-50 text-black font-display font-black uppercase tracking-widest py-3 transition-colors">
        {busy ? "Sending..." : "Request product"}
      </button>
    </form>
  );
}
